import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AuthApiService } from '../../services/auth-api.service';
import { RegisterUserRequest, ApiResult, RegisterUserResponse, AgencyRegistrationRequest, CorporateRegistrationRequest } from '../../types/auth.types';
import { MaterialModule } from '../../shared/material.module';
import { environment } from '../../../environments/environment';
import { MultiStepRegistrationComponent, RegistrationData } from '../../components/shared/multi-step-registration/multi-step-registration.component';

@Component({
  selector: 'app-unregistered',
  standalone: true,
  imports: [CommonModule, MaterialModule, MultiStepRegistrationComponent],
  templateUrl: './unregistered.component.html'
})
export class UnregisteredComponent implements OnInit {
  isLoading = false;
  firebaseUser: any = null;
  returnUrl: string = '/dashboard';
  
  // Message display state
  currentMessage: {
    type: 'error' | 'success' | 'warning' | 'info';
    title: string;
    message: string;
    canRetry?: boolean;
  } | null = null;

  // Environment URLs for template access
  urls = {
    logoUrl: `${environment.appUrl}/logo.png`,
    mainWebsite: environment.appUrl,
    contactUrl: `${environment.appUrl}/contact`,
    companyWebUrl: environment.companyWebUrl,
    termsOfService: `${environment.appUrl}/terms`,
    privacyPolicy: `${environment.appUrl}/privacy`,
    support: `${environment.appUrl}/support`,
  };

  constructor(
    private authService: AuthService,
    private authApiService: AuthApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    console.log('🔧 [UserSetup] === USER SETUP COMPONENT INITIALIZED ===');

    // Get return URL
    this.returnUrl =
      this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    console.log('🔧 [UserSetup] Return URL:', this.returnUrl);

    // Get Firebase user data
    this.firebaseUser = this.authService.getFirebaseUser();
    console.log('🔧 [UserSetup] Firebase user:', this.firebaseUser);

    if (!this.firebaseUser) {
      console.warn(
        '⚠️ [UserSetup] No Firebase user found, redirecting to login'
      );
      this.router.navigate(['/login']);
      return;
    }
    
    // Log the current auth service user to understand the flow
    const currentUser = this.authService.currentUser;
    console.log('🔧 [UserSetup] Current AuthService user:', currentUser);
  }

  async onRegistrationSubmit(registrationData: RegistrationData): Promise<void> {
    this.isLoading = true;
    console.log('🔧 [UserSetup] === SUBMITTING REGISTRATION ===');
    console.log('🔧 [UserSetup] Registration data:', registrationData);

    try {
      // Prepare registration data for API
      const apiRegistrationData: RegisterUserRequest = {
        mobile: registrationData.personalInfo.mobileNumber.replace(/\s/g, ''), // Remove spaces
        email: registrationData.personalInfo.email,
        name: registrationData.personalInfo.displayName,
        type: registrationData.personalInfo.accountType,
        emailVerified: this.firebaseUser.emailVerified || false,
        mobileVerified: false,
      };

      // Add organization details if applicable
      if (registrationData.organizationInfo) {
        if (registrationData.personalInfo.accountType === 'agency') {
          const agency: AgencyRegistrationRequest = {
            name: registrationData.organizationInfo.name,
            ownerUid: this.firebaseUser.uid,
            plan: 'starter', // Default plan for agencies
            panNumber: registrationData.organizationInfo.panNumber,
            gstNumber: registrationData.organizationInfo.gstNumber,
            address: registrationData.organizationInfo.address,
            city: registrationData.organizationInfo.city,
            state: registrationData.organizationInfo.state,
            pincode: registrationData.organizationInfo.pincode,
          };
          apiRegistrationData.agency = agency;
        } else if (registrationData.personalInfo.accountType === 'corporate') {
          const corporate: CorporateRegistrationRequest = {
            name: registrationData.organizationInfo.name,
            ownerUid: this.firebaseUser.uid,
            plan: 'business', // Default plan for corporate
            panNumber: registrationData.organizationInfo.panNumber,
            gstNumber: registrationData.organizationInfo.gstNumber,
            corporateIdentificationNumber: registrationData.organizationInfo.cin, // Map from 'cin' field
            address: registrationData.organizationInfo.address,
            city: registrationData.organizationInfo.city,
            state: registrationData.organizationInfo.state,
            pincode: registrationData.organizationInfo.pincode,
            companyType: registrationData.organizationInfo.companyType as any,
          };
          apiRegistrationData.corporate = corporate;
        }
      }

      console.log('🔧 [UserSetup] Final API registration data:', apiRegistrationData);

      // Call the AuthApiService to register the user with proper error handling
      this.authApiService.registerUser(apiRegistrationData).subscribe({
        next: (result: ApiResult<RegisterUserResponse>) => {
          if (result.success && result.data) {
            console.log('✅ [UserSetup] User registered successfully via API:', result.data);
            
            // Clear any previous messages
            this.clearMessage();

            // Update the auth service with the complete user data
            this.authService.updateCurrentUser({
              ...this.firebaseUser,
              email: registrationData.personalInfo.email,
              displayName: registrationData.personalInfo.displayName,
              mobileNumber: registrationData.personalInfo.mobileNumber,
              accountType: registrationData.personalInfo.accountType,
              role: 'user',
              emailVerified: this.firebaseUser.emailVerified || false,
              profileCompleted: true,
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString(),
            });

            this.showSuccess('Account setup completed successfully!');

            // Navigate to return URL after a short delay to show success message
            setTimeout(() => {
              console.log('🔧 [UserSetup] Navigating to:', this.returnUrl);
              this.router.navigate([this.returnUrl]);
            }, 1500);
          } else {
            // Handle API failure response
            console.error('❌ [UserSetup] API returned failure result:', result);
            let errorMessage = 'Registration failed. Please try again.';
            
            if (result.error?.userMessage) {
              errorMessage = result.error.userMessage;
            } else if (result.error) {
              errorMessage = this.authApiService.getErrorMessage(result.error);
            }
            
            this.showError(errorMessage);
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('❌ [UserSetup] Error registering user via API:', error);
          this.isLoading = false;
          
          // Use the enhanced error handling from AuthApiService
          let errorMessage = 'Failed to setup account. Please try again.';
          let messageType: 'error' | 'warning' = 'error';
          let canRetry = true;
          
          if (error?.userFriendlyError) {
            // Use the user-friendly error message if available
            errorMessage = error.userFriendlyError.message || error.userFriendlyError.title;
            messageType = error.userFriendlyError.type === 'warning' ? 'warning' : 'error';
            canRetry = error.userFriendlyError.canRetry;
          } else if (error?.error?.userMessage) {
            // Use business error user message
            errorMessage = error.error.userMessage;
          } else {
            // Fallback to AuthApiService error message extraction
            errorMessage = this.authApiService.getErrorMessage(error);
          }

          // Show appropriate message type
          this.showMessage(messageType, this.getErrorTitle(messageType), errorMessage, canRetry);

          // Check if we need to redirect to login
          if (this.authApiService.shouldRedirectToLogin(error)) {
            console.warn('⚠️ [UserSetup] Authentication error, redirecting to login');
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000); // Give user time to read the error message
          }
        }
      });

    } catch (error) {
      console.error('❌ [UserSetup] Unexpected error during registration:', error);
      this.isLoading = false;
      this.showError('An unexpected error occurred. Please try again.');
    }
  }

  onCancel(): void {
    console.log('🔧 [UserSetup] User cancelled setup');
    // Sign out the user since they didn't complete setup
    this.authService.signOut();
  }

  /**
   * Helper method to display messages with different types
   */
  private showMessage(type: 'error' | 'success' | 'warning' | 'info', title: string, message: string, canRetry: boolean = false): void {
    this.currentMessage = {
      type,
      title,
      message: this.sanitizeErrorMessage(message),
      canRetry
    };
    
    console.log(`📢 [UserSetup] Displaying ${type} message:`, this.currentMessage.message);
  }

  /**
   * Helper method to display error messages consistently
   */
  private showError(errorMessage: string): void {
    this.showMessage('error', 'Registration Failed', errorMessage, true);
  }

  /**
   * Helper method to display success messages
   */
  private showSuccess(message: string): void {
    this.showMessage('success', 'Success!', message);
  }

  /**
   * Get appropriate title for error type
   */
  private getErrorTitle(type: 'error' | 'warning'): string {
    switch (type) {
      case 'warning':
        return 'Attention Required';
      case 'error':
      default:
        return 'Registration Failed';
    }
  }

  /**
   * Clear all message state
   */
  clearMessage(): void {
    this.currentMessage = null;
  }

  /**
   * Clean up error messages to be more user-friendly
   */
  private sanitizeErrorMessage(errorMessage: string): string {
    // Remove technical details that might confuse users
    const technicalPatterns = [
      /error:\s*/i,
      /exception:\s*/i,
      /\[.*?\]/g, // Remove brackets with content
      /code:\s*\d+/i,
      /status:\s*\d+/i
    ];

    let cleanMessage = errorMessage;
    technicalPatterns.forEach(pattern => {
      cleanMessage = cleanMessage.replace(pattern, '').trim();
    });

    // Ensure the message starts with a capital letter and ends with a period
    if (cleanMessage) {
      cleanMessage = cleanMessage.charAt(0).toUpperCase() + cleanMessage.slice(1);
      if (!cleanMessage.endsWith('.') && !cleanMessage.endsWith('!') && !cleanMessage.endsWith('?')) {
        cleanMessage += '.';
      }
    }

    // Fallback to generic message if cleaning results in empty string
    return cleanMessage || 'An error occurred while setting up your account. Please try again.';
  }
}
